// ** Reactstrap Imports
import {
    Badge,
    Breadcrumb,
    BreadcrumbItem
} from "reactstrap";
import { useEffect, useRef, useState } from "react";

import CustomTooltip from "@components/ui-components/tooltip";
import Flex from "@components/ui-components/flex";
import { Link } from "react-router-dom";
// ** Third Party Imports
import LongNameFormatter from "@components/formatters/long-name-formatter";
import {
    BackPanel,
    PanelUserDropdown
} from "@components/navbar-common";
import Proptypes from "prop-types";
import classnames from "classnames";
import { useTranslation } from "react-i18next";
import useWindowSize from "@hooks/use-window-size";
import MaintenanceBanner from "@src/layouts/components/menu/maintainance-alert";

const Breadcrumbs = props => {
    const { t } = useTranslation();
    const { items = [], rightChildren = null, bottomChildren = null } = props;
    const { isMobileView } = useWindowSize();
    const [showToolTip, setShowToolTip] = useState(false);
    const textRef = useRef(null);

    useEffect(() => {
        const textContainer = textRef.current;
        if (textContainer) {
            const isOverflowing =
        textContainer.scrollWidth > textContainer.clientWidth;
            setShowToolTip(isOverflowing);
        }
    }, [isMobileView, items]);

    return (
        <>
            <div
                data-cy="breadcrumb"
                className={classnames("breadcrumb-header", {
                    "custom-header"           : bottomChildren,
                    "custom-breadcrumb-height": !bottomChildren
                })}>
                {isMobileView ? (
                    <>
                        <Flex column className="overflow-hidden w-100">
                            <Flex className="w-100" justify="between" align="center">
                                <Breadcrumb className="breadcrumb-chevron">
                                    {items.map((i, index) => {
                                        const id = i.title?.replaceAll(/[^A-Za-z0-9_]/g, "");
                                        return (
                                            i && (
                                                <BreadcrumbItem
                                                    data-cy={`breadcrumb-item-${index}`}
                                                    active={i.active}
                                                    key={index}
                                                    className="overflow-hidden">
                                                    {i.active ? (
                                                        <>
                                                            <div
                                                                ref={textRef}
                                                                className="fw-bolder text-truncate"
                                                                data-cy={`title-${id}`}
                                                                id={`title-${id}`}>
                                                                {t(i.title)}
                                                            </div>
                                                            {showToolTip ? (
                                                                <CustomTooltip
                                                                    target={`title-${id}`}
                                                                    content={i.title}/>
                                                            ) : null}
                                                        </>
                                                    ) : (
                                                        <Link
                                                            to={i.to}
                                                            className="text-primary d-none d-sm-block">
                                                            <LongNameFormatter
                                                                content={i.title}
                                                                testId={i.title}
                                                                max={30}
                                                                id={i.title}
                                                                className="text-truncate"/>
                                                        </Link>
                                                    )}
                                                </BreadcrumbItem>
                                            )
                                        );
                                    })}
                                </Breadcrumb>
                                <Flex
                                    justify="end"
                                    className="d-flex w-100 h-100"
                                    align="center">
                                    {rightChildren}
                                </Flex>
                            </Flex>
                            {bottomChildren ? (
                                <Flex
                                    className="w-100 overflow-scroll custom-scroll"
                                    align="center">
                                    {bottomChildren}
                                </Flex>
                            ) : null}
                        </Flex>
                    </>
                ) : (
                    <>
                        <Flex column className="overflow-hidden w-auto">
                            <Breadcrumb className="breadcrumb-chevron">
                                {items.map((i, index) => {
                                    const id = i.title?.replaceAll(/[^A-Za-z0-9_]/g, "");
                                    return (
                                        i && (
                                            <BreadcrumbItem
                                                data-cy={`breadcrumb-item-${index}`}
                                                active={i.active}
                                                key={index}
                                                className="overflow-hidden">
                                                {i.active ? (
                                                    <Flex align="center">
                                                        <div
                                                            ref={textRef}
                                                            className="fw-bolder text-truncate text-primary"
                                                            id={`title-${id}`}>
                                                            {t(i.title)}
                                                        </div>
                                                        {showToolTip ? (
                                                            <CustomTooltip
                                                                target={`title-${id}`}
                                                                content={i.title}/>
                                                        ) : null}
                                                        {i.beta ? <> <Badge
                                                            color="primary"
                                                            className="font-small-3 cursor-pointer"
                                                            id="beta-badge">
                                                            {t("Beta")}
                                                        </Badge>
                                                        <CustomTooltip
                                                            target="beta-badge"
                                                            content="This feature is still in progress but the user can access the basic functionality for approving the rejected events"/>
                                                        </> : null}
                                                    </Flex>
                                                ) : (
                                                    <Link
                                                        to={i.to}
                                                        className="text-primary d-none d-sm-block">
                                                        <LongNameFormatter
                                                            content={i.title}
                                                            max={30}
                                                            id={i.title}
                                                            className="text-truncate"/>
                                                    </Link>
                                                )}
                                            </BreadcrumbItem>
                                        )
                                    );
                                })}
                            </Breadcrumb>
                            {bottomChildren ? (
                                <div className="mb-25">
                                    {bottomChildren}
                                </div>
                            ) : null}
                       
                        </Flex>
                        
                        <Flex className="w-max" justify="end" align="center">
                            <BackPanel />
                            <Flex className="d-flex w-max h-100">
                                {rightChildren && (
                                    <Flex justify="end" align="center">
                                        {" "}
                                        {rightChildren}
                                    </Flex>
                                )}
                                <PanelUserDropdown />
                            </Flex>
                        </Flex>
                    </>  
                )}
            </div>
            <MaintenanceBanner/>
        </>
    );
};

export default Breadcrumbs;

Breadcrumbs.propTypes = {
    items         : Proptypes.arrayOf(Proptypes.object),
    rightChildren : Proptypes.any,
    bottomChildren: Proptypes.any
};
